import apiClient from "../../apiClient";
import { authFetch } from "../../csrf";
import { API_ENDPOINTS, STORAGE_KEYS } from "../../config";

const quotationService = {

  /**
   * Create a new quotation (with attachments)
   */
  createQuotation: async (formData) => {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.CRM.QUOTATIONS,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      );

      return {
        success: response.responseCode === 201,
        quotation: response.data
      };

    } catch (error) {
      
      return {
        success: false,
        error: error.message || error.response?.data?.responseMessage || "Failed to create quotation",
      };
    }
  },

  /**
   * Update quotation (with attachments)
   */
  updateQuotation: async (quoteId, formData) => {
    try {
      const response = await apiClient.patch(
        `${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      );

      return {
        success: response.responseCode === 200,
        quotation: response.data
      };

    } catch (error) {
      
      return {
        success: false,
        error: error.message || error.response?.data?.responseMessage || "Failed to update quotation",
      };
    }
  },

  /**
   * Finalize quotation
   */
  finalizeQuotation: async (quoteId) => {
    try {
      const response = await apiClient.patch(
        `${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}/finalize`,
        {}
      );

      return {
        success: response.responseCode === 200,
        quotation: response.data
      };

    } catch (error) {
      
      return {
        success: false,
        error: error.message || error.response?.data?.responseMessage || "Failed to finalize quotation"
      };
    }
  },

  /**
   * Send quotation via email
   */
  sendQuotation: async (quoteId, emailData) => {
    try {
      const response = await apiClient.post(
        `${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}/send`,
        emailData
      );

      return {
        success: response.responseCode === 200,
        messageId: response.data.messageId,
        pdfUrl: response.data.pdfUrl
      };

    } catch (error) {
      
      return {
        success: false,
        error: error.message || error.response?.data?.responseMessage || "Failed to send quotation"
      };
    }
  },

  /**
   * Get single quotation
   */
  getQuotationById: async (quoteId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();

      // Add any additional params (e.g., ordered=true)
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          queryParams.append(key, params[key]);
        }
      });

      const queryString = queryParams.toString();
      const url = queryString
        ? `${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}?${queryString}`
        : `${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}`;

      const response = await apiClient.get(url);

      return {
        success: response.responseCode === 200,
        quotation: response.data
      };

    } catch (error) {
      
      return {
        success: false,
        error: error.message || error.response?.data?.responseMessage || "Failed to retrieve quotation"
      };
    }
  },

  /**
   * Export quotation/order detail as backend-generated Excel (.xlsx)
   * and trigger browser download. Pass { ordered: true } for order pages.
   */
  exportQuotationDetail: async (quoteId, fallbackName = 'Quotation', params = {}) => {
    try {
      if (!quoteId) {
        return { success: false, error: 'Quotation ID is required' };
      }

      const queryParams = new URLSearchParams();
      Object.keys(params || {}).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          queryParams.append(key, params[key]);
        }
      });
      const queryString = queryParams.toString();
      const url = queryString
        ? `${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}/export?${queryString}`
        : `${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}/export`;

      // Use apiClient (withCredentials + CSRF) — same pattern as other detail exports.
      const blob = await apiClient.get(url, { responseType: 'blob' });

      const isOrdered = params.ordered === true || params.ordered === 'true';
      const prefix = isOrdered ? 'Order' : 'Quotation';
      const sanitized = String(fallbackName || prefix)
        .replace(/[\\/:*?"<>|]/g, '')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 80) || prefix;
      const filename = `${prefix}_${sanitized}.xlsx`;

      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || error.response?.data?.responseMessage || 'Failed to export quotation details'
      };
    }
  },

  /**
   * Get quotations under an opportunity
   */
  getQuotationsByOpportunity: async (opportunityId, filters = {}) => {
    try {
      const params = new URLSearchParams();
      params.append("opportunityId", opportunityId);
      if (filters.status) params.append("status", filters.status);

      const response = await apiClient.get(
        `${API_ENDPOINTS.CRM.QUOTATIONS}?${params.toString()}`
      );

      return {
        success: response.responseCode === 200,
        quotations: Array.isArray(response.data) ? response.data : []
      };

    } catch (error) {
      
      return {
        success: false,
        quotations: [],
        error: error.message || error.response?.data?.responseMessage || "Failed to retrieve quotations"
      };
    }
  },

  /**
   * Get quotations by customer ID with pagination
   */
  getQuotationsByCustomer: async (customerId, params = {}) => {
    try {
      const { page = 1, limit = 10 } = params;
      const queryParams = new URLSearchParams();
      queryParams.append('page', page);
      queryParams.append('limit', limit);

      const response = await apiClient.get(
        `${API_ENDPOINTS.CRM.QUOTATIONS}/by-customer/${customerId}?${queryParams.toString()}`
      );

      return {
        success: response.responseCode === 200,
        data: response.data?.quotations || [],
        pagination: response.data?.pagination || {}
      };

    } catch (error) {
      
      return {
        success: false,
        error: error.message || error.response?.data?.responseMessage || "Failed to retrieve quotations",
        data: [],
        pagination: {}
      };
    }
  },

  /**
   * Delete quotation
   */
  deleteQuotation: async (quoteId) => {
    try {
      const response = await apiClient.delete(
        `${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}`
      );

      return {
        success: response.responseCode === 200,
        message: response.responseMessage || "Quotation deleted successfully"
      };

    } catch (error) {
      
      return {
        success: false,
        error: error.message || error.response?.data?.responseMessage || "Failed to delete quotation"
      };
    }
  },

  /**
   * Download quotation PDF
   */
  downloadQuotation: async (quoteId) => {
    try {
      const response = await authFetch(
        `${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}/download-quotation`,
        {
          method: "GET",
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to download quotation");
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("content-disposition");
      let fileName = `Quotation_${quoteId}_${new Date().toISOString().split('T')[0]}.pdf`;

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/i);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = decodeURIComponent(fileNameMatch[1]);
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return {
        success: true,
        message: 'Quotation downloaded successfully'
      };

    } catch (error) {
      
      return {
        success: false,
        error: error.response?.data?.responseMessage || "Failed to download quotation"
      };
    }
  },

  /**
   * Send WhatsApp message
   */
  sendWhatsApp: async (data) => {
    try {
      const response = await apiClient.post(
        `${API_ENDPOINTS.CRM.QUOTATIONS}/whatsapp/send`,
        data
      );

      return {
        success: response.success,
        data: response.data,
        message: response.responseMessage
      };
    } catch (error) {
      
      return {
        success: false,
        error: error.message || error.response?.data?.responseMessage || "Failed to send WhatsApp message"
      };
    }
  },

  /**
   * Download invoice PDF
   */
  downloadInvoice: async (quoteId) => {
    try {
      let fileName = `Invoice_${quoteId}_${new Date().toISOString().split('T')[0]}.pdf`;
      const blob = await apiClient.get(
        `${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}/download-invoice`,
        { responseType: 'blob' }
      );

      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return {
        success: true,
        message: 'Invoice downloaded successfully'
      };

    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to download invoice"
      };
    }
  },

  /**
   * Delete attachment from quotation
   */
  deleteAttachment: async (quoteId, attachmentId) => {
    try {
      const response = await apiClient.delete(
        `${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}/attachments/${attachmentId}`
      );

      return {
        success: response.responseCode === 200,
        message: response.responseMessage || "Attachment deleted successfully"
      };

    } catch (error) {
      
      return {
        success: false,
        error: error.message || error.response?.data?.responseMessage || "Failed to delete attachment"
      };
    }
  }
};

export default quotationService;
